import { FormEvent, Fragment, useEffect, useRef, useState } from "react";
import { Row, Col, Form, Card, Button, Pagination, Badge, OverlayTrigger, Tooltip } from "react-bootstrap";
import { useSearchParams } from "react-router-dom";
import AbpnSpinner from "../../Layout/Components/AbpnSpinner";
import Banner from "../../Layout/Components/Banner";
import { getDateWithOffset } from "../../Layout/Components/GetDateWithOffset";
import { ValidatorHelper } from "../../Layout/Components/ValidatorHelper";
import { abpnClient } from "../../Services/AbpnClient";
import { Certificate, CertificationGeneration, CertificateStatus } from "../Models/Certificate";
import { Certification } from "../Models/Certification";
import PhysicianSearchParameters from "../Models/PhysicianSearchParameters";
import { PhysicianSearchResultSet, PhysicianSearchResult } from "../Models/PhysicianSearchResult";
import { StateProvince } from "../Models/StateProvince";
import CertificatesCard from "./CertificatesCard";

export default function PhysicianSearch() {
    const enum SearchType {
        name = 1,
        location = 2,
        certificate = 3,
        npi = 4
    }

    const lastNameElementId = "lastNameElementId";
    var lastNameElement: HTMLInputElement = null;

    const cityElementId = "cityElementId";
    var cityElement: HTMLInputElement = null;

    const stateElementId = "stateElementId";
    var stateElement: HTMLInputElement = null;

    const certificateNumberElementId = "certificateNumberElementId";
    var certificateNumberElement: HTMLInputElement = null;

    const certificationCertNumberElementId = "certificationCertNumberElementId";
    var certificationCertNumberElement: HTMLInputElement = null;

    const certificationCityStateElementId = "certificationCityStateElementId";
    var certificationCityStateElement: HTMLInputElement = null;

    const npiElementId = "npiElementId";
    var npiElement: HTMLInputElement = null;

    var resultsRef = useRef(null);

    const [searchParams, setSearchParams] = useSearchParams();

    var [physicianSearchParams, setPhysicianSearchParams] = useState<PhysicianSearchParameters>(null);

    var [searchResults, setSearchResults] = useState<PhysicianSearchResultSet>(null);
    var [certifications, setCertifications] = useState<Array<Certification>>([]);
    var [stateProvinces, setStateProvinces] = useState<Array<StateProvince>>([]);
    var [isLoading, setIsLoading] = useState<boolean>(false);
    var [showDetailsPhysicianId, setShowDetailsPhysicianId] = useState<number>(null);
    var [validationMessage, setValidationMessage] = useState<string>("");
    var [paginationStartingPage, setPaginationStartingPage] = useState<number>(1);

    useEffect(() => {
        const getCertifications = async () => {
            const results = await abpnClient.get<Array<Certification>>(`/api/verifyCert/certifications`);
            setCertifications(results);
        };
        const getStates = async () => {
            const results = await abpnClient.get<Array<StateProvince>>(`/api/verifyCert/states`);
            setStateProvinces(results);
        };
        var defaultSearch = new PhysicianSearchParameters(searchParams);

        getCertifications();
        getStates();
        setPhysicianSearchParams(defaultSearch);
    }, []);

    useEffect(() => {
        const queryString = searchParams.toString();
        if (queryString.length === 0) return;

        const setPagination = () => {
            var currentSearch = new PhysicianSearchParameters(searchParams);

            var startingPaginationNumber = (currentSearch.pageNumber ?? 0) - 5;
            if (startingPaginationNumber < 1) startingPaginationNumber = 1;
            setPaginationStartingPage(startingPaginationNumber);
        }
        const getPhysicians = async () => {
            setIsLoading(true);
            const results = await abpnClient.get<PhysicianSearchResultSet>(`/api/verifyCert/search?${queryString}`);
            setSearchResults(results);
            if (results.physicians.length === 1) {
                setShowDetailsPhysicianId(results.physicians[0].physicianId);
            }
            setIsLoading(false);
        };
        getPhysicians();
        setPagination();
    }, [searchParams])

    useEffect(() => {
        if (resultsRef.current != null) resultsRef.current.scrollIntoView();
    }, [isLoading])

    const updateSearchParameters = (updateFunction: (n: PhysicianSearchParameters) => void, searchType: SearchType) => {
        clearFormValidation();
        var updated = getSearchParams(searchType, null);
        updateFunction(updated);
        setPhysicianSearchParams(updated);
    }

    const onSubmit = async (e: FormEvent<HTMLFormElement>, searchType: SearchType) => {
        e.preventDefault();
        e.stopPropagation();
        validateForm(searchType);

        e.currentTarget.classList.add('was-validated');
        if (e.currentTarget.checkValidity()) {
            e.currentTarget.classList.remove('was-validated');

            var searchParams = getSearchParams(searchType, null);
            saveSearchParams(searchParams);
        }
    }

    const getSearchParams = (searchType: SearchType, pageNumber: number) => {
        var params = new PhysicianSearchParameters();
        switch (searchType) {
            case SearchType.name: {
                params.lastName = physicianSearchParams.lastName;
                params.certificationId = physicianSearchParams.lNameCertificationId;
                params.lNameCertificationId = physicianSearchParams.lNameCertificationId;
                params.firstName = physicianSearchParams.firstName;

                params.certCertificationId = null;
                params.stateCertificationId = null;
                break;
            }
            case SearchType.location: {
                params.city = physicianSearchParams.city;
                params.stateId = physicianSearchParams.stateId;
                params.certificationId = physicianSearchParams.stateCertificationId;
                params.stateCertificationId = physicianSearchParams.stateCertificationId;

                params.certCertificationId = null;
                params.lNameCertificationId = null;
                break;
            }
            case SearchType.certificate: {
                params.certificationId = physicianSearchParams.certCertificationId;
                params.certificateNumber = physicianSearchParams.certificateNumber;
                params.certCertificationId = physicianSearchParams.certCertificationId;

                params.lNameCertificationId = null;
                params.stateCertificationId = null;
                break;
            }
            case SearchType.npi: {
                params.npi = physicianSearchParams.npi;
                break;
            }
            default: {
                break;
            }
        }
        if (pageNumber == null) return params;

        var currentSearchParams = new PhysicianSearchParameters(searchParams);

        params.pageNumber = pageNumber;
        params.certificationId = currentSearchParams.certificationId;
        return params;
    }
    const clearFormValidation = () => {
        lastNameElement = document.getElementById(lastNameElementId) as HTMLInputElement;
        cityElement = document.getElementById(cityElementId) as HTMLInputElement;
        stateElement = document.getElementById(stateElementId) as HTMLInputElement;
        certificateNumberElement = document.getElementById(certificateNumberElementId) as HTMLInputElement;
        certificationCertNumberElement = document.getElementById(certificationCertNumberElementId) as HTMLInputElement;
        certificationCityStateElement = document.getElementById(certificationCityStateElementId) as HTMLInputElement;
        npiElement = document.getElementById(npiElementId) as HTMLInputElement;

        ValidatorHelper.clearCustomValidation(lastNameElement);
        ValidatorHelper.clearCustomValidation(cityElement);
        ValidatorHelper.clearCustomValidation(stateElement);
        ValidatorHelper.clearCustomValidation(certificateNumberElement);
        ValidatorHelper.clearCustomValidation(certificationCertNumberElement);
        ValidatorHelper.clearCustomValidation(certificationCityStateElement);
        ValidatorHelper.clearCustomValidation(npiElement);
        setValidationMessage("");
    }
    const validateForm = (searchType: SearchType) => {
        clearFormValidation();

        if (searchType == SearchType.name) {
            validateNameSearch();
            return;
        }

        if (searchType == SearchType.location) {
            validateCityStateSearch();
            return;
        }

        if (searchType == SearchType.certificate) {
            validateCertificateSearch();
            return;
        }

        if (searchType == SearchType.npi) {
            validateNpiSearch();
            return;
        }

        setValidationMessage("Search criteria is required");
    }

    const validateNpiSearch = () => {
        if (!physicianSearchParams.npi) {
            ValidatorHelper.setCustomValidationWithFeedback(npiElement, "Npi is required");
        }
    }
    const validateNameSearch = () => {
        if (!physicianSearchParams.lastName) {
            ValidatorHelper.setCustomValidationWithFeedback(lastNameElement, "Last name is required");
        }
    }

    const validateCityStateSearch = () => {
        var stateIdHasValue = (!isNaN(physicianSearchParams.stateId) && physicianSearchParams.stateId > 0);
        var hasAllRequiredFields = (physicianSearchParams.city && doesCertificationIdHaveValue(physicianSearchParams.stateCertificationId) && stateIdHasValue)
        if (!hasAllRequiredFields) {
            if (!stateIdHasValue) {
                ValidatorHelper.setCustomValidationWithFeedback(stateElement, "State is required");
            }
            if (!doesCertificationIdHaveValue(physicianSearchParams.stateCertificationId)) {
                ValidatorHelper.setCustomValidationWithFeedback(certificationCityStateElement, "Certification is required");
            }
            if (!physicianSearchParams.city) {
                ValidatorHelper.setCustomValidationWithFeedback(cityElement, "City is required");
            }
        }
    }
    const doesCertificationIdHaveValue = (certificationId: number) => {
        return (!isNaN(certificationId) && certificationId > 0)
    }

    const validateCertificateSearch = () => {
        if (!physicianSearchParams.certificateNumber) {
            ValidatorHelper.setCustomValidationWithFeedback(certificateNumberElement, "Certificate Number is required");
        }
        if (!doesCertificationIdHaveValue(physicianSearchParams.certCertificationId)) {
            ValidatorHelper.setCustomValidationWithFeedback(certificationCertNumberElement, "Certification is required");
        }
    }

    const getAddressDisplay = (physician: PhysicianSearchResult) => {
        if (physician.city && physician.stateCode) return `${physician.city}, ${physician.stateCode}`;

        return "";
    }

    const rankCert = (a: Certificate[], b: Certificate[]) => {
        var isSameStatus = a.some(_ => _.certificateStatus == CertificateStatus.Active) === b.some(_ => _.certificateStatus == CertificateStatus.Active);
        var isSameSpecialtyType = a[0].isPrimary === b[0].isPrimary;

        if (isSameStatus && isSameSpecialtyType) {
            let aAwardDates = a.map(_ => getDateWithOffset(_.awardDate).getTime());
            let bAwardDates = b.map(_ => getDateWithOffset(_.awardDate).getTime());
            return Math.max(...aAwardDates) - Math.max(...bAwardDates);
        }
        else if (isSameStatus && !isSameSpecialtyType) {
            //primary second
            return a[0].isPrimary ? -1 : 1;
        }
        else {
            //active first
            return a.some(_ => _.certificateStatus == CertificateStatus.Active) ? -1 : 1;
        }
    }

    const getSortedCertificates = (physician: PhysicianSearchResult) => {
        let groupedCertsBySpecialty: Record<number, Array<Certificate>> = {};

        groupedCertsBySpecialty = physician.certificates.reduce(
            (result, item) => ({
                ...result,
                [item["certificationId"]]: [
                    ...(result[item["certificationId"]] || []),
                    item,
                ],
            }),
            groupedCertsBySpecialty);

        return Object.values(groupedCertsBySpecialty).sort(rankCert);
    }

    const saveSearchParams = (params: PhysicianSearchParameters) => {
        let arr = [] as any[];
        Object.keys(params).forEach(key => {
            if ((params as any)[key] && key !== "stateCertificationId" && key !== "lNameCertificationId" && key !== "certCertificationId")
                arr.push([key, (params as any)[key]]);
        });
        setSearchParams(arr);
    }

    const togglePhysicianDetails = (physicianId: number) => {
        var newValue = showDetailsPhysicianId === physicianId ? null : physicianId;
        setShowDetailsPhysicianId(newValue);
    }

    const getCurrentName = (physician: PhysicianSearchResult) => {
        return getFullName(physician.lastName, physician.firstName, physician.middleInitial, physician.suffix);
    }
    
    const getFullName = (lastName: string, firstName: string, middleInitial: string, suffix: string) => {
        return `${lastName}, ${firstName} ${middleInitial ? " " + middleInitial : ""}${suffix ? " " + suffix : ""}`
    }

    

    const getCertInfoIcon = (certificate: Certificate) => {
        const certification = certifications.find(_ => _.id === certificate.certificationId);
        const isHpmCertificate = certificate.isHPM;

        if (!certification.hasMaintainDependency &&
            certificate.certificationGeneration !== CertificationGeneration.Cc &&
            certificate.certificationGeneration !== CertificationGeneration.Lifetime) return null;

        return (<OverlayTrigger
            placement={"right"}
            delay={{ hide: 800 } as any}
            overlay={<Tooltip>
                {certification.hasMaintainDependency && !isHpmCertificate &&
                    <p><i className="las la-info-circle text-info"></i> Subspecialty with a dependency on a primary Specialty for certification status. See <a className="link-primary" target="_blank" rel="noreferrer" href="https://www.abpn.org/become-certified/taking-a-subspecialty-exam/">ABPN.org</a> for more information.</p>
                }
                {certificate.certificationGeneration === CertificationGeneration.Cc && !isHpmCertificate &&
                    <p><i className="las la-info-circle text-primary"></i> Certification contingent on meeting CC criteria. See <a className="link-primary" target="_blank" rel="noreferrer" href="https://www.abpn.org/maintain-certification/maintenance-of-certification-program/10-year-and-c-moc-programs/cmoc-program/">ABPN.org</a> for more information.</p>
                }
                {certificate.certificationGeneration === CertificationGeneration.Lifetime && !isHpmCertificate &&
                    <p><i className="las la-info-circle text-light"></i> Lifetime certification - holders are not required to meet CC/MOC requirements. Please visit <a className="link-primary" target="_blank" rel="noreferrer" href="https://www.abpn.org/maintain-certification/maintenance-of-certification-program/lifetime-certificate-holders/">ABPN.org</a> for more information.</p>
                }
                {isHpmCertificate &&
                    <p><i className="las la-info-circle text-light"></i> The ABPN issued certificates in Hospice and Palliative Medicine (HPM) from 2008-2021.  In 2022, HPM certificates issued by ABPN were transitioned to the American Board of Internal Medicine (ABIM) for initial certification and continuing certification.</p>
                }
            </Tooltip>}>
            <i className="las la-info-circle text-light"></i>
        </OverlayTrigger>)
    }

    let orRow = <Row>
        <Col xs={{ span: 8, offset: 2 }}>
            <div className="w-100 mt-3 mb-3 text-muted" style={{ height: "12px", borderBottom: "1px solid", textAlign: "center" }}>
                <span className="p-2 bg-white">
                    OR
                </span>
            </div>
        </Col>
    </Row>;

    var submitButton = <Row>
        <Col xs={12} className="text-center">
            <Button variant="primary" disabled={isLoading} className="mt-4 text-light" type="submit">Search</Button>
        </Col>
    </Row>;

    const getSearchType = () => {
        var currentSearchParams = new PhysicianSearchParameters(searchParams);

        if (currentSearchParams.lastName) return SearchType.name;
        if (currentSearchParams.stateId) return SearchType.location;
        if (currentSearchParams.certificateNumber) return SearchType.certificate;
        if (currentSearchParams.npi) return SearchType.npi;

        return null;
    }
    const updatePageNumber = (pageNumber: number) => {
        var searchType = getSearchType();
        var updated = getSearchParams(searchType, pageNumber);

        var newPaginationStartingPage = pageNumber > 10 ? pageNumber - 5 : 1;
        setPaginationStartingPage(newPaginationStartingPage);
        setPhysicianSearchParams(updated);
        saveSearchParams(updated);
    }

    var getAllPages = () => {
        //@ts-ignore
        return Array.from({ length: searchResults.totalPages }, (value: number, index: any) => value = index + 1);
    }

    var getClickablePages = () => {
        var range = getAllPages();

        var startingIndex = paginationStartingPage - 1;

        var nextTenPages = startingIndex + 10;
        var lastPages = range.length - 1;

        var endingIndex = range.length > 10 ? nextTenPages : lastPages;

        return range.slice(startingIndex, endingIndex + 1);
    }

    var getNextPages = () => {
        var lastPage = searchResults.totalPages

        var newStartingPage = paginationStartingPage + 10;
        if (newStartingPage > lastPage) newStartingPage = lastPage;
        setPaginationStartingPage(newStartingPage);
    }

    var getPreviousPages = () => {
        var newStartingPage = paginationStartingPage - 10;
        if (newStartingPage < 1) newStartingPage = 1;
        setPaginationStartingPage(newStartingPage);
    }

    var hasNextPages = () => {
        var lastPage = searchResults.totalPages
        var currentLastPage = getLastPage();
        return currentLastPage < lastPage;
    }
    var getLastPage = () => {
        var currentPages = getClickablePages();
        return currentPages[currentPages.length - 1];
    }
    var getFirstPage = () => {
        var currentPages = getClickablePages();
        return currentPages[0];
    }
    var hasPreviousPages = () => {
        var firstPage = getFirstPage();
        return firstPage > 1;
    }

    var getCertificationSelections = () => {
        return <Fragment>
            <option value="">Select a certification...</option>
            <optgroup label="Specialties">
                {
                    certifications.filter(_ => _.isPrimary && _.isActive).sort((a, b) => b.name < a.name ? -1 : 1).map(certification => {
                        return <option key={certification.id}
                            value={certification.id}>{certification.name}
                        </option>
                    })
                }
            </optgroup>
            <optgroup label="Subspecialties">
                {
                    certifications.filter(_ => !_.isPrimary && _.isActive).map(certification => {
                        return <option key={certification.id}
                            value={certification.id}>{certification.name}
                        </option>
                    })
                }
            </optgroup>
            <optgroup label="Previously Issued">
                {
                    certifications.filter(_ => !_.isActive).map(certification => {
                        return <option key={certification.id}
                            value={certification.id}>{certification.name}
                        </option>
                    })
                }
            </optgroup>
        </Fragment>
    }

    if (physicianSearchParams == null) return <AbpnSpinner />;
    return (
        <Card className="border-vc-secondary-grey">
            <Card.Body>
                <h1 className="text-secondary">verifyCERT®</h1>
                <Card.Subtitle className="text-primary mb-4 fw-bold">Certification and Status Verification</Card.Subtitle>
                <Banner />
                <Row>
                    <Col xs={12} md={4} xl={6}>
                        <p className="card-text mt-3">Welcome to verifyCERT®.  ABPN’s verifyCERT® provides you with the ability to identify physicians who have been certified by the ABPN (diplomates)
                            and view the status of ABPN diplomates' certificates.</p>
                        <p className="card-text">The ABPN issues specialty certificates in psychiatry, neurology, and neurology with
                            special qualification in child neurology.</p>
                        <p className="card-text">Diplomates certified in a specialty may also achieve certification in a subspecialty. The ABPN
                            issues subspecialty certificates in: </p>
                        <Row>
                            <Col>
                                <ul className="text-capitalize">
                                    <li>addiction psychiatry</li>
                                    <li>brain injury medicine</li>
                                    <li>child and adolescent psychiatry</li>
                                    <li>clinical neurophysiology</li>
                                    <li>consultation-liaison psychiatry (formerly, psychosomatic medicine)</li>
                                    <li>epilepsy</li>
                                    <li>forensic psychiatry</li>
                                    <li>geriatric psychiatry</li>
                                </ul>
                            </Col>
                            <Col>
                                <ul className="text-capitalize">
                                    <li>hospice and palliative medicine*</li>
                                    <li>neurocritical care</li>
                                    <li>neurodevelopmental disabilities</li>
                                    <li>neuromuscular medicine</li>
                                    <li>pain medicine</li>
                                    <li>sleep medicine</li>
                                    <li>vascular neurology</li>
                                </ul>
                            </Col>
                        </Row>
                        <p className="card-text">Please visit <a className="link-primary" href="https://abpn.org">abpn.org</a> for more information on initial certification and continuing certification
                            processes.</p>
                        <p className="card-text mt-4 vc-font-size-smallest">
                            *ABPN issued certificates in Hospice and Palliative Medicine (HPM) from 2008-2021.
                            In 2022, HPM certificates issued by ABPN were transitioned to the American Board of Internal Medicine (ABIM) for initial
                            certification and continuing certification.
                        </p>
                    </Col>
                    <Col xs={12} md={8} xl={6}>
                        <Card className="vc-search-card">
                            <Card.Body>
                                <Card.Title className="text-primary fw-bold">Board Certified Physician Search</Card.Title>
                                <Card.Subtitle className="mb-4">Check if a Psychiatrist or Neurologist is ABPN Board Certified</Card.Subtitle>
                                <Form noValidate onSubmit={(e) => onSubmit(e, SearchType.name)}>
                                    <Row>
                                        <Col xs={12} md={6}>
                                            <Form.Group>
                                                <Form.Label>Last Name
                                                    <span className="text-primary ml-2">*</span></Form.Label>
                                                <Form.Control id={lastNameElementId} size="sm" type="input" placeholder="Doe"
                                                    onChange={(e) => updateSearchParameters(_ => _.lastName = e.target.value, SearchType.name)}
                                                    value={physicianSearchParams.lastName ?? ""} />
                                                <span className="invalid-feedback vc-font-size-smallest"></span>
                                            </Form.Group>
                                        </Col>
                                        <Col xs={12} md={6}>
                                            <Form.Group controlId="FirstName">
                                                <Form.Label>First Name</Form.Label>
                                                <Form.Control size="sm" type="input" placeholder="John"
                                                    onChange={(e) => updateSearchParameters(_ => _.firstName = e.target.value, SearchType.name)}
                                                    value={physicianSearchParams.firstName ?? ""} />
                                            </Form.Group>
                                        </Col>
                                        <Col xs={12} md={6}>
                                            <Form.Group controlId="Certification" className="mt-2">
                                                <Form.Label>Certification</Form.Label>
                                                <Form.Select size="sm"
                                                    value={physicianSearchParams.lNameCertificationId ?? ""}
                                                    onChange={(e) => updateSearchParameters(_ => _.lNameCertificationId = parseInt(e.target.value), SearchType.name)}>
                                                    {getCertificationSelections()}
                                                </Form.Select>
                                                <span className="invalid-feedback vc-font-size-smallest"></span>
                                            </Form.Group>
                                        </Col>
                                    </Row>
                                    {submitButton}
                                </Form>

                                {orRow}
                                <Form noValidate onSubmit={(e) => onSubmit(e, SearchType.location)}>
                                    <Row>
                                        <Col xs={12} md={6}>
                                            <Row>
                                                <Col xs={12}>
                                                    <Form.Group className="mt-2">
                                                        <Form.Label>State/Area
                                                            <span className="text-primary ml-2">*</span></Form.Label>
                                                        <Form.Select id={stateElementId} size="sm"
                                                            onChange={(e) => updateSearchParameters(_ => _.stateId = parseInt(e.target.value), SearchType.location)}
                                                            value={physicianSearchParams.stateId ? physicianSearchParams.stateId : ""}>
                                                            <option value="">Select a state...</option>
                                                            {stateProvinces.map(x => (<option value={x.id} key={x.id}>{x.name}</option>))}
                                                        </Form.Select>
                                                        <span className="invalid-feedback vc-font-size-smallest"></span>
                                                    </Form.Group>
                                                </Col>
                                            </Row>
                                        </Col>
                                        <Col xs={12} md={6}>
                                            <Form.Group className="mt-2">
                                                <Form.Label>City</Form.Label>
                                                <Form.Control id={stateElementId} size="sm" type="input" placeholder="Chicago"
                                                    onChange={(e) => updateSearchParameters(_ => _.city = e.target.value, SearchType.location)}
                                                    value={physicianSearchParams.city ?? ""} />
                                                <span className="invalid-feedback vc-font-size-smallest"></span>
                                            </Form.Group>
                                        </Col>
                                        <Col xs={12} md={6}>
                                            <Form.Group className="mt-2">
                                                <Form.Label>Certification
                                                    <span className="text-primary ml-2">*</span></Form.Label>
                                                <Form.Select size="sm"
                                                    id={certificationCityStateElementId}
                                                    value={physicianSearchParams.stateCertificationId ?? ""}
                                                    onChange={(e) => updateSearchParameters(_ => _.stateCertificationId = parseInt(e.target.value), SearchType.location)}>
                                                    {getCertificationSelections()}
                                                </Form.Select>
                                                <span className="invalid-feedback vc-font-size-smallest"></span>
                                            </Form.Group>
                                        </Col>
                                    </Row>
                                    {submitButton}
                                </Form>

                                {orRow}

                                <Form noValidate onSubmit={(e) => onSubmit(e, SearchType.certificate)}>
                                    <Row>
                                        <Col xs={12} md={6}>
                                            <Form.Group className="mt-2">
                                                <Form.Label>Certificate Number
                                                    <span className="text-primary ml-2">*</span></Form.Label>
                                                <Form.Control size="sm" type="number" id={certificateNumberElementId} placeholder=""
                                                    onChange={(e) => updateSearchParameters(_ => _.certificateNumber = parseInt(e.target.value), SearchType.certificate)}
                                                    value={physicianSearchParams.certificateNumber ?? ""} />
                                                <span className="invalid-feedback vc-font-size-smallest"></span>
                                            </Form.Group>
                                        </Col>
                                        <Col xs={12} md={6}>
                                            <Form.Group className="mt-2">
                                                <Form.Label>Certification
                                                    <span className="text-primary ml-2">*</span></Form.Label>
                                                <Form.Select size="sm"
                                                    id={certificationCertNumberElementId}
                                                    value={physicianSearchParams.certCertificationId ?? ""}
                                                    onChange={(e) => updateSearchParameters(_ => _.certCertificationId = parseInt(e.target.value), SearchType.certificate)}>
                                                    {getCertificationSelections()}
                                                </Form.Select>
                                                <span className="invalid-feedback vc-font-size-smallest"></span>
                                            </Form.Group>
                                        </Col>
                                    </Row>
                                    {submitButton}
                                </Form>

                                {orRow}

                                <Form noValidate onSubmit={(e) => onSubmit(e, SearchType.npi)}>
                                    <Row>
                                        <Col xs={12} md={6}>
                                            <Form.Group controlId="NationalProviderIdentifier" className="mt-2 mb-3">
                                                <Form.Label>National Provider Identifier (NPI)</Form.Label>
                                                <Form.Control id={npiElementId} size="sm" type="input" placeholder=""
                                                    onChange={(e) => updateSearchParameters(_ => _.npi = e.target.value, SearchType.npi)}
                                                    value={physicianSearchParams.npi ?? ""} />
                                                <span className="invalid-feedback vc-font-size-smallest"></span>
                                            </Form.Group>
                                        </Col>
                                        <p className="vc-font-size-smallest">
                                        The ABPN does not have an NPI for all physicians.  If your search by NPI returns 0 results, please search again using different search criteria before concluding that the physician you are searching for is not certified.
                                        </p>
                                    </Row>
                                    {submitButton}
                                </Form>

                                <Row>
                                    <Col xs={12} md={12} className="text-center mt-3">
                                        <span className="text-danger">{validationMessage}</span>
                                    </Col>
                                </Row>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
                <div ref={resultsRef} className="vc-search-results pt-4">
                    {searchResults == null && isLoading && <AbpnSpinner />}
                    {searchResults != null && <Fragment>
                        <Card.Title className="text-primary fw-bold mt-4">Search Results</Card.Title>
                        <Card.Text className="mb-4">Active, full, and unrestricted medical licensure is a requirement to maintain certification by the ABPN. Licensure data may be reported to the ABPN through the Federation of State Medical Boards, individual state medical licensing boards, or self-reported by physicians. The ABPN cannot guarantee the certification status resulting from the loss or restriction of licensure if the loss or restriction has not been reported to the ABPN. Continuing Certification (CC) participation includes meeting all components of the CC Program as outlined ABPN.org.                            The information in verifyCERT® is current as of the date indicated, and is for certificates issued by the ABPN. New diplomates will not appear until examintion grades are released, which is approximately 12 weeks following each examination. For information about certificates issued by other Boards, contact the ABMS or the individual issuing Board.</Card.Text>
                        <Card.Subtitle className="text-primary mb-2">Status as of {new Date().toLocaleDateString()}</Card.Subtitle>
                        <hr />
                        {isLoading && <AbpnSpinner />}
                        {!isLoading && searchResults.physicians?.length <= 0 && <span>No results</span>}
                        {!isLoading && searchResults.physicians?.map(physician => {
                            var sortedCerts = getSortedCertificates(physician);
                            var showDetails = showDetailsPhysicianId === physician.physicianId;
                            var activeCerts = sortedCerts.filter((_) => _.some(_ => _.certificateStatus == CertificateStatus.Active));
                            var addressDisplay = getAddressDisplay(physician);
                            return (
                                <div key={physician.physicianId}>
                                    <div className="d-flex justify-content-between">
                                        <span className="d-flex justify-content-start">
                                            <Card.Title className="text-secondary me-3">{getCurrentName(physician)}</Card.Title>
                                            {activeCerts.map(cert => { return <span key={cert[0].certificateNumber}><Badge className="vc-clickable-badge" onClick={() => togglePhysicianDetails(physician.physicianId)} bg="secondary">{cert[0].certificationTitle} {getCertInfoIcon(cert[0])}</Badge> &nbsp;</span> })}
                                        </span>
                                        <div>
                                            <small className="text-secondary">ABPN ID: {physician.physicianId} &nbsp;</small>
                                        </div>
                                    </div>
                                    <div className="d-flex justify-content-between m-0">
                                        <span className="d-flex flex-column justify-content-start">
                                            {addressDisplay && <Card.Subtitle className="text-secondary mb-2">{addressDisplay}</Card.Subtitle>}
                                        </span>
                                        <span className="align-self-start">
                                            <Card.Subtitle className="text-secondary mb-2">
                                                <button className="btn-sm btn btn-link" onClick={() => togglePhysicianDetails(physician.physicianId)}>{showDetails ? "Hide" : "Show"} Certificate Details</button>
                                            </Card.Subtitle>
                                        </span>
                                    </div>
                                    <hr />
                                    <CertificatesCard sortedAndGroupedCertificates={sortedCerts} isOnlyResult={searchResults.physicians.length === 1} showDetails={showDetails} physician={physician} />
                                </div>
                            )
                        })}
                        {!isLoading && searchResults.totalPages > 1 &&
                            <div className="d-flex justify-content-center w-100">
                                <Pagination className="mt-3">
                                    <Pagination.Prev
                                        disabled={!hasPreviousPages()}
                                        key={"leftArrow"}
                                        onClick={() => getPreviousPages()}></Pagination.Prev>
                                    {getClickablePages().map((pageNumber: number) => {
                                        return <Pagination.Item
                                            active={pageNumber === (physicianSearchParams.pageNumber ?? 1)}
                                            key={pageNumber}
                                            onClick={() => updatePageNumber(pageNumber)}>{pageNumber}</Pagination.Item>
                                    })}
                                    <Pagination.Next
                                        disabled={!hasNextPages()}
                                        key={"rightArrow"}
                                        onClick={() => getNextPages()}></Pagination.Next>
                                </Pagination>
                            </div>
                        }
                    </Fragment>}
                </div>
            </Card.Body>
        </Card >
    );
}
